import { useCallback, useEffect, useRef, useState, type PointerEvent } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

/**
 * A signature pad for the booth consent (CONFERENCE-INTAKE). Pointer Events,
 * so a finger, a stylus and a mouse all draw; the backing canvas is scaled
 * by devicePixelRatio so strokes stay crisp on a tablet. `onChange` gets a
 * PNG data URL after every stroke, and null once cleared.
 *
 * Dependency-free on purpose — a signature is a few polylines.
 */
export function SignaturePad({
  label,
  onChange,
  height = 200,
  disabled = false,
}: {
  label?: string;
  onChange: (dataUrl: string | null) => void;
  height?: number;
  disabled?: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const last = useRef<{ x: number; y: number } | null>(null);
  const [empty, setEmpty] = useState(true);
  // Latest onChange without re-running the sizing effect when the parent
  // passes a fresh callback each render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });
  const emptyRef = useRef(true);
  const markEmpty = (v: boolean) => {
    emptyRef.current = v;
    setEmpty(v);
  };

  // Size the backing store to the element × devicePixelRatio. Resizing a
  // canvas wipes it, so this only runs on mount and when the width changes
  // (e.g. the tablet rotates) — and then the signature has to be redone.
  const fit = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const width = canvas.clientWidth || 600;
    const w = Math.round(width * dpr);
    const h = Math.round(height * dpr);
    if (canvas.width === w && canvas.height === h) return;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = '#111';
    }
    if (!emptyRef.current) {
      markEmpty(true);
      onChangeRef.current(null);
    }
  }, [height]);

  useEffect(() => {
    fit();
    if (typeof ResizeObserver === 'undefined' || !canvasRef.current) return;
    const ro = new ResizeObserver(() => fit());
    ro.observe(canvasRef.current);
    return () => ro.disconnect();
  }, [fit]);

  const point = (e: PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPointerDown = (e: PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    const p = point(e);
    last.current = p;
    // A tap leaves a dot, so a one-touch initial still registers.
    const ctx = e.currentTarget.getContext('2d');
    if (ctx) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 1.25, 0, Math.PI * 2);
      ctx.fillStyle = '#111';
      ctx.fill();
    }
  };

  const onPointerMove = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || !last.current) return;
    e.preventDefault();
    const ctx = e.currentTarget.getContext('2d');
    const p = point(e);
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(last.current.x, last.current.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    }
    last.current = p;
  };

  const end = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    markEmpty(false);
    onChangeRef.current(e.currentTarget.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
    }
    markEmpty(true);
    onChangeRef.current(null);
  };

  return (
    <Stack spacing={0.5}>
      {label && (
        <Typography variant="subtitle2" component="div">
          {label}
        </Typography>
      )}
      <Box
        sx={{
          position: 'relative',
          border: 2,
          borderColor: empty ? 'divider' : 'primary.main',
          borderRadius: 1,
          // Always white: the PNG is stored and printed on white.
          bgcolor: '#fff',
        }}
      >
        <canvas
          ref={canvasRef}
          aria-label={label ?? 'Signature'}
          data-testid="signature-canvas"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={end}
          onPointerCancel={end}
          onPointerLeave={end}
          style={{
            display: 'block',
            width: '100%',
            height,
            // Stop the browser scrolling/zooming while signing with a finger.
            touchAction: 'none',
            cursor: disabled ? 'default' : 'crosshair',
          }}
        />
        {empty && (
          <Typography
            color="text.disabled"
            sx={{
              position: 'absolute',
              left: 16,
              bottom: 12,
              pointerEvents: 'none',
              borderTop: 1,
              borderColor: 'text.disabled',
              pt: 0.5,
              pr: 8,
            }}
          >
            Sign here
          </Typography>
        )}
      </Box>
      <Stack direction="row" justifyContent="flex-end">
        <Button onClick={clear} disabled={disabled || empty} size="small">
          Clear
        </Button>
      </Stack>
    </Stack>
  );
}
